import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import { useState } from "react"
import { toast } from "sonner"
import { Button } from "@holigral/shared/ui/components/ui/button"
import { BusySpinner } from "@holigral/shared/ui/composed"
import { useReplacePaymentMethodMutation } from "./api"
import { useT } from "@/i18n"

type Props = {
  readonly onSaved: () => void
}

/**
 * In-app card replacement (product.md §6.1, no Stripe portal): confirm the
 * SetupIntent the dialog created, then promote the new card to default via the
 * backend.
 */
export default function ReplaceCardForm({ onSaved }: Props) {
  const { t } = useT("money")
  const stripe = useStripe()
  const elements = useElements()
  const [replacePaymentMethod] = useReplacePaymentMethodMutation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    if (!stripe || !elements) {
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}${window.location.pathname}`,
        },
        redirect: "if_required",
      })
      if (result.error) {
        setError(result.error.message ?? t("topUp.genericError"))

        return
      }

      await replacePaymentMethod({
        setupIntentId: result.setupIntent.id,
      }).unwrap()
      toast.success(t("paymentMethod.replaceSuccess"))
      onSaved()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PaymentElement />
      {!!error && <p className="text-sm text-destructive">{error}</p>}
      <Button variant="primary" onClick={save} disabled={!stripe || busy}>
        <BusySpinner busy={busy} />
        {t("paymentMethod.replaceCta")}
      </Button>
    </div>
  )
}
